import {CalendarEntryData, NonNullInterval} from "../services/calendar/CalendarEntryData";
import {DateTime, IANAZone, Interval} from "luxon";
import {Calendar} from "../domain/CalendarDto";
import {BusyStatusEnum} from "../domain/BusyStatusEnum";
import {MeetingStatusEnum} from "../domain/MeetingStatusEnum";
import {AttendeeStatusEnum} from "../domain/AttendeeStatusEnum";
import {SensitivityEnum} from "../domain/SensitivityEnum";
import {LocalZone, UtcZone} from "../util/DateHelper";


export class CalendarInviteParser {
    private _value: string | null = null;
    private _extendedHeader: boolean = false;
    private _header: string | null = null;
    private _timezone: string | null = null;

    parse(text: string): CalendarEntryData | null {
        const calendar = this._parseText(text);

        try {
            const intervalLocal = Interval.fromDateTimes(calendar.StartDateTime!.setZone(calendar.TimezoneName), calendar.EndDateTime!.setZone(calendar.TimezoneName)) as NonNullInterval;
            const intervalUtc = Interval.fromDateTimes(calendar.StartDateTime!.toUTC(), calendar.EndDateTime!.toUTC()) as NonNullInterval;
            return {
                calendar,
                recurrence: null,
                instanceId: 0,
                intervalLocal,
                intervalUtc,
                colSpan: 0,
                colStart: 0,
                row: 0,
            };
        } catch (e) {
            console.error("Failed to parse calendar", calendar, e);
        }
        return null
    }

    private _parseText(text: string): Calendar {
        const lines = text.split("\n");
        // Combine lines that span multiple lines into one
        const headers: string[] = [];
        let currentHeader: string | null = null;
        for (let line of lines) {
            const end = line.endsWith("\r") ? line.length - 1 : line.length;
            if (line.startsWith("\t") || line.startsWith(" ")) {
                currentHeader += line.substring(1, end);
            } else if (line.length > 0) {
                if (currentHeader) {
                    headers.push(currentHeader);
                }
                currentHeader = line.substring(0, end);
            }
        }
        if (currentHeader) {
            headers.push(currentHeader);
        }

        const result: Calendar = {
            AllDayEvent: false,
            AppointmentReplyDateTime: null,
            Attendees: [],
            Body: "",
            BusyStatus: BusyStatusEnum.Busy,
            Category: null,
            ClientId: undefined,
            CreateDateTime: null,
            DisallowNewTimeProposal: false,
            EndDateTime: null,
            Exceptions: [],
            Id: -1,
            IsHtml: false,
            IsOwner: false,
            IsPublic: false,
            Location: null,
            MeetingStatus: MeetingStatusEnum.Other,
            OnlineMeetingConfLink: null,
            OnlineMeetingExternalLink: null,
            OrganizerEmail: null,
            OrganizerName: null,
            Recurrences: [],
            ReminderMins: null,
            ResponseRequested: false,
            ResponseType: AttendeeStatusEnum.ResponseUnknown,
            Sensitivity: SensitivityEnum.Normal,
            StartDateTime: null,
            Subject: "",
            Timezone: LocalZone as IANAZone,
            TimezoneName: UtcZone.name,
            Uid: "",
        };

        const context: string[] = [];
        let startTime: ExtendedData = {value: "", extendedData: null};
        let endTime: ExtendedData = {value: "", extendedData: null};

        // Process the combined lines
        for (const line of headers) {
            if (!this.parseLine(line)) {
                continue;
            }
            if (this._header === "begin" && this._value) {
                context.push(this._value);
            } else if (this._header === "end" && context.length) {
                // const popped =
                context.splice(context.length - 1, 1);
                // if (popped === "VTIMEZONE")
            } else if (context.length) {
                const current = context[context.length - 1];
                if (current === "VEVENT") {
                    switch (this._header) {
                        case "summary":
                            result.Subject = this.handleValue().value;
                            break;
                        case "description":
                            result.Body = this.handleValue().value;
                            break;
                        case "uid":
                            result.Uid = this.handleValue().value;
                            break;
                        case "organizer":
                            const organizer = this.handleValue();
                            result.OrganizerName = this.parseOrganiserName(organizer.extendedData);
                            result.OrganizerEmail = this.parseOrganiserEmail(organizer.value);
                            break;
                        case "location":
                            result.Location = this.handleValue().value;
                            break;
                        case "url":
                            result.OnlineMeetingExternalLink = this.handleValue().value;
                            break;

                        case "x-microsoft-cdo-busystatus":
                            result.BusyStatus = this.parseBusyStatus(this.handleValue().value);
                            break;

                        case "dtstart":
                            startTime = this.handleValue();
                            break;
                        case "dtend":
                            endTime = this.handleValue();
                            break;
                    }
                } else if (current === "VALARM") {
                    switch (this._header) {
                        case "trigger":
                            result.ReminderMins = this.parseReminder(this.handleValue());
                            break;
                    }
                }
            }
        }
        result.StartDateTime = this.convertDateTime(startTime);
        result.EndDateTime = this.convertDateTime(endTime);

        result.TimezoneName = this._timezone ?? UtcZone.name;
        result.Timezone = IANAZone.create(result.TimezoneName);

        return result;
    }

    private parseLine(line: string): boolean {
        this._extendedHeader = false;
        let sep1 = line.indexOf(':');
        const sep2 = line.indexOf(';');
        if (sep1 === -1) {
            this._value = null;
            return false;
        }
        if (sep2 > -1 && sep2 < sep1) {
            this._extendedHeader = true;
            sep1 = sep2;
        }

        this._header = line.substring(0, sep1).toLowerCase();

        this._value = sep1 + 1 < line.length
            ? line.substring(sep1 + 1).trimStart()
            : "";
        return true;
    }

    private handleValue(): ExtendedData {
        if (this._value === null) {
            return {value: "", extendedData: null};
        }
        let value = this._value;
        let extendedData: string | null = null;
        // TODO: extended headers?
        if (this._extendedHeader) {
            const colonIndex = this._value.indexOf(':');
            if (colonIndex > -1) {
                extendedData = this._value.substring(0, colonIndex);
                value = this._value.substring(colonIndex + 1);
            }
        }

        value = value.replaceAll("\\n", "\r\n").replaceAll("\\,", ",");
        return {
            value,
            extendedData,
        };
    }

    private convertDateTime(time: ExtendedData): DateTime | null {
        if (!time.value) {
            return null;
        }
        let dateTimeToParse = time.value.trim();
        if (time.extendedData && time.extendedData.includes("TZID")) {
            let timezoneId = time.extendedData.substring(time.extendedData.lastIndexOf('=') + 1);
            if (timezoneId.startsWith('"') && timezoneId.length > 1) {
                timezoneId = timezoneId.substring(1, timezoneId.length - 2);
            }

            this._timezone = this.convertTimeZone(timezoneId);
            return DateTime.fromFormat(dateTimeToParse, "yyyyMMdd'T'HHmmss", {zone: this._timezone});
        }
        return null;
    }

    private parseOrganiserName(value: string | null) {
        if (value) {
            value = value.trim();
            return value.substring(value.indexOf('=') + 1).trim();
        }
        return null;
    }

    private parseOrganiserEmail(value: string) {
        value = value.trim();
        if (value && value.startsWith("mailto:")) {
            return value.substring(7).trim();
        }
        return value;
    }

    private parseReminder({value}: ExtendedData) {
        // TRIGGER;RELATED=START:-PT20M
        value = value.trim();
        if (!value) {
            return 15;
        }
        // TODO: support more VALARM types/settings
        let multiplier = -1;
        if (value.startsWith("-")) {
            multiplier = 1;
            value = value.substring(1);
        }

        if (value.endsWith("D")) {
            multiplier *= 24 * 60;
            value = value.substring(0, value.length - 1);
        }

        if (value.endsWith("M")) {
            value = value.substring(0, value.length - 1);
        }

        let result = 15;
        if (value.startsWith("PT")) {
            result = parseInt(value.substring(2));
        } else if (value.startsWith("P")) {
            result = parseInt(value.substring(1));
        }

        return multiplier * result;
    }

    private parseBusyStatus(value: string) {
        return BusyStatusMap[value] ?? BusyStatusEnum.Busy;
    }

    private convertTimeZone(timezoneId: string | null) {
        if (timezoneId === "GMT Standard Time") {
            return "Europe/London";
        }
        // TODO: find a way of converting these...
        return UtcZone.name;
    }
}

const BusyStatusMap: Record<string, BusyStatusEnum> = {
    "FREE": BusyStatusEnum.Busy,
    "OOF": BusyStatusEnum.OutOfOffice,
    "TENTATIVE": BusyStatusEnum.Tentative,
};

interface ExtendedData {
    value: string;
    extendedData: string | null;
}